// Honest comparison of a candidate prompt against its baseline on the same items.
//
// Every item is scored under both prompts, so the results are PAIRED: analyse the per-item
// difference, which cancels item difficulty (Miller, "Adding Error Bars to Evals", 2024).
// The headline delta is field-pooled (matched / total fields per side), so the bootstrap
// resamples whole ITEMS (the item is the cluster) and recomputes exactly that delta.
//
// Small samples make percentile-bootstrap intervals too narrow (Hesterberg 2015), so the
// reported interval is the WIDER of the bootstrap and a t-interval on per-item deltas. A
// direction is only claimed once at least MIN_CHANGED items moved: below 6 non-tied
// items a two-sided sign test cannot reach 95% even if every change agrees. Seeded, so a
// run always shows the same interval.

import type { ExtractionScore } from './scoring'

/**
 * improved / regressed: the interval excludes zero.
 * equivalent: the interval sits inside ±margin (no meaningful difference).
 * inconclusive: none of the above; the data cannot tell.
 * identical: every item scored the same under both prompts.
 * too-few: too few items, or too few items that changed, to judge a direction.
 */
export type Verdict = 'improved' | 'regressed' | 'equivalent' | 'inconclusive' | 'identical' | 'too-few'

export type PairedComparison = {
  verdict: Verdict
  items: number
  /** Field-pooled candidate minus baseline accuracy (the headline delta). */
  delta: number
  /** 95% interval for `delta`; null when there are too few items to form one. */
  interval: { low: number; high: number } | null
  /** Smallest difference worth acting on; `equivalent` means the interval sits inside it. */
  margin: number
  /** Smallest true difference this run could detect about 80% of the time. */
  detectable: number | null
  improved: number
  regressed: number
  tied: number
}

export const MIN_ITEMS = 6
export const MIN_CHANGED = 6
/** Default smallest meaningful difference, 2 points, when no noise check has measured one. */
export const DEFAULT_MARGIN = 0.02
const RESAMPLES = 10000
const EPS = 1e-9

/** The part of a score the statistics use, so a stage or field can stand in for the whole. */
export type Tally = Pick<ExtractionScore, 'overall' | 'matched' | 'total'>

type Pair = { candidate: Tally; baseline: Tally }

/** Two-sided 95% t critical values by degrees of freedom; lookups round df DOWN (wider). */
const T_975: [number, number][] = [
  [1, 12.706],
  [2, 4.303],
  [3, 3.182],
  [4, 2.776],
  [5, 2.571],
  [6, 2.447],
  [7, 2.365],
  [8, 2.306],
  [9, 2.262],
  [10, 2.228],
  [12, 2.179],
  [15, 2.131],
  [20, 2.086],
  [25, 2.06],
  [30, 2.042],
  [40, 2.021],
  [60, 2.0],
  [120, 1.98],
]

function tCritical(df: number): number {
  const row = [...T_975].reverse().find(([tabulated]) => tabulated <= df)
  return row ? row[1] : T_975[0][1]
}

/** Small deterministic PRNG (mulberry32) so intervals do not jitter between renders. */
function seededRandom(seedText: string): () => number {
  let seed = 2166136261
  for (let i = 0; i < seedText.length; i++) seed = Math.imul(seed ^ seedText.charCodeAt(i), 16777619)
  return () => {
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Each side pools over its own field count, as the headline does: a prompt that adds an
 *  extra person scores more fields than one that does not. */
function pooledDelta(pairs: Pair[]): number {
  let candidateMatched = 0
  let candidateTotal = 0
  let baselineMatched = 0
  let baselineTotal = 0
  for (const { candidate, baseline } of pairs) {
    candidateMatched += candidate.matched
    candidateTotal += candidate.total
    baselineMatched += baseline.matched
    baselineTotal += baseline.total
  }
  return (
    (candidateTotal ? candidateMatched / candidateTotal : 1) - (baselineTotal ? baselineMatched / baselineTotal : 1)
  )
}

export function comparePaired(pairs: Pair[], seed: string, margin = DEFAULT_MARGIN): PairedComparison {
  const differences = pairs.map((pair) => pair.candidate.overall - pair.baseline.overall)
  const improved = differences.filter((difference) => difference > EPS).length
  const regressed = differences.filter((difference) => difference < -EPS).length
  const tied = pairs.length - improved - regressed
  const delta = pooledDelta(pairs)
  const base = { items: pairs.length, delta, margin, improved, regressed, tied }

  if (pairs.length > 0 && improved === 0 && regressed === 0) {
    return { ...base, verdict: `identical`, interval: { low: 0, high: 0 }, detectable: null }
  }
  if (pairs.length < MIN_ITEMS) return { ...base, verdict: `too-few`, interval: null, detectable: null }

  const n = pairs.length
  const mean = differences.reduce((sum, difference) => sum + difference, 0) / n
  const sd = Math.sqrt(differences.reduce((sum, difference) => sum + (difference - mean) ** 2, 0) / (n - 1))
  const t = tCritical(n - 1)
  const halfWidth = (t * sd) / Math.sqrt(n)

  const random = seededRandom(seed)
  const resampled = Array.from({ length: RESAMPLES }, () =>
    pooledDelta(Array.from({ length: n }, () => pairs[Math.floor(random() * n)])),
  ).sort((a, b) => a - b)
  const interval = {
    low: Math.min(resampled[Math.floor(RESAMPLES * 0.025)], delta - halfWidth),
    high: Math.max(resampled[Math.ceil(RESAMPLES * 0.975) - 1], delta + halfWidth),
  }
  const detectable = ((t + 0.84) * sd) / Math.sqrt(n)

  const verdict: Verdict =
    interval.low >= -margin && interval.high <= margin
      ? `equivalent`
      : improved + regressed < MIN_CHANGED
        ? `too-few`
        : interval.low > EPS
          ? `improved`
          : interval.high < -EPS
            ? `regressed`
            : `inconclusive`
  return { ...base, verdict, interval, detectable }
}

/**
 * A percentage with only the precision the data supports: whole percent below 100 items,
 * so "93%" on 10 calls rather than a falsely precise "92.86%".
 */
export function formatAccuracy(value: number, items: number): string {
  return `${(value * 100).toFixed(items >= 100 ? 1 : 0)}%`
}

/** Signed points with matching precision, e.g. "+3", "−1.5", "±0". */
export function formatPoints(delta: number, items: number): string {
  const decimals = items >= 100 ? 1 : 0
  const rounded = Number((delta * 100).toFixed(decimals))
  if (rounded === 0) return `±0`
  return `${rounded > 0 ? `+` : `−`}${Math.abs(rounded).toFixed(decimals)}`
}

/**
 * 95% range for one prompt's field-pooled accuracy over a run's items: the wider of an
 * item-level bootstrap and a t-interval on per-item accuracy, clamped to 0–100%. With few
 * items this is wide, which is the point: a 4-call score is not a precise number.
 */
export function accuracyRange(scores: Tally[], seed: string): { low: number; high: number } {
  const matched = scores.reduce((sum, score) => sum + score.matched, 0)
  const total = scores.reduce((sum, score) => sum + score.total, 0)
  const pooled = total ? matched / total : 1
  const n = scores.length
  if (n < 2) return { low: pooled, high: pooled }

  const mean = scores.reduce((sum, score) => sum + score.overall, 0) / n
  const sd = Math.sqrt(scores.reduce((sum, score) => sum + (score.overall - mean) ** 2, 0) / (n - 1))
  const halfWidth = (tCritical(n - 1) * sd) / Math.sqrt(n)

  const random = seededRandom(seed)
  const resampled = Array.from({ length: RESAMPLES }, () => {
    let sampleMatched = 0
    let sampleTotal = 0
    for (let i = 0; i < n; i++) {
      const score = scores[Math.floor(random() * n)]
      sampleMatched += score.matched
      sampleTotal += score.total
    }
    return sampleTotal ? sampleMatched / sampleTotal : 1
  }).sort((a, b) => a - b)

  return {
    low: Math.max(0, Math.min(resampled[Math.floor(RESAMPLES * 0.025)], pooled - halfWidth)),
    high: Math.min(1, Math.max(resampled[Math.ceil(RESAMPLES * 0.975) - 1], pooled + halfWidth)),
  }
}

/** A 95% t-interval on a mean, never below zero: for per-call cost and latency. */
export function meanRange(values: number[]): { mean: number; low: number; high: number } {
  const n = values.length
  const mean = n ? values.reduce((sum, value) => sum + value, 0) / n : 0
  if (n < 2) return { mean, low: mean, high: mean }
  const sd = Math.sqrt(values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / (n - 1))
  const halfWidth = (tCritical(n - 1) * sd) / Math.sqrt(n)
  return { mean, low: Math.max(0, mean - halfWidth), high: mean + halfWidth }
}
